import { motion } from "framer-motion";
import { Check } from "lucide-react";

import { STEP_LABELS } from "@/hooks/useStrategyState";

/**
 * Horizontal step indicator for the Strategy wizard.
 *
 * - Completed dots: mint fill + checkmark
 * - Current dot: electric-blue accent + ring glow
 * - Future dots: muted
 *
 * Fully controlled; parent passes `current` (1-indexed) and `completedUpTo`.
 */
export default function StepIndicator({ current, completedUpTo = 0 }) {
    return (
        <div
            className="flex w-full items-center justify-between gap-2"
            data-testid="strategy-step-indicator"
        >
            {STEP_LABELS.map((label, idx) => {
                const n = idx + 1;
                const done = n <= completedUpTo && n < current;
                const active = n === current;
                return (
                    <div
                        key={label}
                        className="flex flex-1 items-center"
                        data-testid={`strategy-step-dot-${n}`}
                        data-state={done ? "done" : active ? "active" : "future"}
                    >
                        <div className="flex min-w-0 flex-1 items-center gap-2">
                            <div className="relative">
                                <motion.div
                                    layout
                                    className={[
                                        "relative flex h-7 w-7 shrink-0 items-center justify-center rounded-full border font-mono text-[10px] font-semibold tabular-nums transition-colors",
                                        done &&
                                            "border-[#00F5D4] bg-[#00F5D4] text-[#0B0F19]",
                                        active &&
                                            "border-[#2F58CD] bg-[#111726] text-[#E8ECF5]",
                                        !done &&
                                            !active &&
                                            "border-[#1F2A40] bg-[#111726] text-[#8A93A6]",
                                    ]
                                        .filter(Boolean)
                                        .join(" ")}
                                    style={
                                        active
                                            ? {
                                                  boxShadow:
                                                      "0 0 18px rgba(47,88,205,0.35), inset 0 0 0 1px rgba(0,245,212,0.3)",
                                              }
                                            : undefined
                                    }
                                >
                                    {done ? (
                                        <Check
                                            className="h-3.5 w-3.5"
                                            strokeWidth={3}
                                        />
                                    ) : (
                                        n
                                    )}
                                </motion.div>
                            </div>
                            <div className="hidden min-w-0 sm:block">
                                <span
                                    className={[
                                        "block truncate font-mono text-[10px] uppercase tracking-[0.22em] transition-colors",
                                        active
                                            ? "text-[#E8ECF5]"
                                            : done
                                              ? "text-[#00F5D4]"
                                              : "text-[#8A93A6]",
                                    ].join(" ")}
                                >
                                    {label}
                                </span>
                            </div>
                        </div>

                        {idx < STEP_LABELS.length - 1 && (
                            <div
                                className={[
                                    "mx-2 h-px flex-1 transition-colors",
                                    done ? "bg-[#00F5D4]/60" : "bg-[#1F2A40]",
                                ].join(" ")}
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
}
